import type { ChatSendPayload } from "./chatTypes";

export type QueuedMessage = ChatSendPayload & {
  id: string;
  state?: "sent" | "failed";
};

function queueStorageKey(chatId: string) {
  return `chat-queue:${chatId}`;
}

export function loadQueue(chatId: string): QueuedMessage[] {
  try {
    const raw = localStorage.getItem(queueStorageKey(chatId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as QueuedMessage[];
    if (!Array.isArray(parsed)) return [];
    return parsed.map((m) => ({
      id: m.id,
      text: m.text,
      isDictated: m.isDictated,
      planMode: m.planMode,
      chatModel: m.chatModel,
      selectedStep: m.selectedStep,
      attachments: m.attachments,
    }));
  } catch {
    return [];
  }
}

// The queue is shared across tabs through localStorage, so releasing a message
// must first claim it: remove it from storage, and treat it as already sent if
// another tab got there first.
export function claimQueued(chatId: string, id: string): boolean {
  const stored = loadQueue(chatId);
  if (!stored.some((m) => m.id === id)) return false;
  saveQueue(
    chatId,
    stored.filter((m) => m.id !== id)
  );
  return true;
}

export function saveQueue(chatId: string, queue: QueuedMessage[]) {
  try {
    const stripped: QueuedMessage[] = queue
      .filter((m) => m.state !== "sent")
      .map((m) => ({
        id: m.id,
        text: m.text,
        isDictated: m.isDictated,
        planMode: m.planMode,
        chatModel: m.chatModel,
        selectedStep: m.selectedStep,
        attachments: m.attachments,
      }));
    if (stripped.length === 0) {
      localStorage.removeItem(queueStorageKey(chatId));
    } else {
      localStorage.setItem(queueStorageKey(chatId), JSON.stringify(stripped));
    }
  } catch {
    // Quota or serialisation issue — give up silently rather than break the UI.
  }
}
